/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ReactNode} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  FilterChecklistOptionBase,
  FilterChecklistValueBase,
  FilterTreeChecklistChangeBase,
  FilterTreeNodeBase
} from '@ion/dls/src/filters/types';
import {BoundaryMode, DateSelectorValues, TrackingMode} from '@ion/dls/src/types/types';

/**
 * Defines stable category ids used by the current tracking filter drawer.
 *
 * ```typescript
 * import {TrackingFilterDrawerCategoryId} from './types';
 *
 * ...
 *
 * const categoryId = TrackingFilterDrawerCategoryId.GroupsAssets;
 * ```
 */
export enum TrackingFilterDrawerCategoryId {
  AssetStatus = 'asset-status',
  AssetType = 'asset-type',
  Category = 'category',
  Favorites = 'favorites',
  Geofence = 'geofence',
  GroupsAssets = 'groups-assets',
  Routes = 'routes'
}

export enum TrackingFilterStateKey {
  AssetCategories = 'assetCategories',
  AssetStatus = 'assetStatus',
  AssetTypes = 'assetTypes',
  Assets = 'assets',
  Devices = 'devices',
  GeoFences = 'geoFences',
  Groups = 'groups',
  Routes = 'routes'
}

export interface TrackingFavoriteFilterBucket {
  selected: any[];
}

/**
 * Defines the History range a favorite was saved over - the shape the legacy
 * theme's saved filters keep it in, so a favorite saved in either theme reads
 * the same in the other.
 */
export interface TrackingFavoriteDateState {
  period: DateSelectorValues;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
}

export interface TrackingFavoriteFilterState extends GenericObject {
  [TrackingFilterStateKey.AssetCategories]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.AssetStatus]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.AssetTypes]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.Assets]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.Devices]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.GeoFences]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.Groups]?: TrackingFavoriteFilterBucket;
  [TrackingFilterStateKey.Routes]?: TrackingFavoriteFilterBucket;
  // the History range - or Live - it was saved over, as legacy saved filters
  // keep it ...
  date?: TrackingFavoriteDateState;
  // the drawn boundary or vicinity search it was saved with, its rings as
  // legacy saved filters keep them, and which of the two it is ...
  boundary?: unknown[];
  boundaryMode?: BoundaryMode;
}

export type TrackingFavoritePreferences = Record<string, TrackingFavoriteFilterState>;

/**
 * Defines the imperative API exposed by current tracking drawer filters.
 *
 * ```typescript
 * import {TrackingDrawerFilterHandleBase} from './types';
 *
 * ...
 *
 * const filterRef = useRef<TrackingDrawerFilterHandleBase>(null);
 * filterRef.current?.apply();
 * ```
 */
export interface TrackingDrawerFilterHandleBase {
  apply: () => void;
  clear: () => void;
  discard: () => void;
  getFavoriteState: () => TrackingFavoriteFilterState;
  hasChanges: () => boolean;
  loadFavoriteState: (state: TrackingFavoriteFilterState) => void;
}

/**
 * Asks the tracking filter drawer to run a navigation away from Tracking -
 * at once when the drawer has no unapplied changes, otherwise once the user
 * confirms leaving Tracking.
 *
 * ```typescript
 * import {TrackingFilterNavigationRequest} from './types';
 *
 * ...
 *
 * const onRequestNavigate: TrackingFilterNavigationRequest = navigate => {
 *   drawerRef.current?.confirmLeave(navigate);
 * };
 * ```
 */
export type TrackingFilterNavigationRequest = (navigate: () => void) => void;

/**
 * Defines the imperative API exposed by the current tracking filter drawer,
 * through which the side nav holds an action that would close the drawer or
 * leave Tracking until the user has decided what to do with unapplied
 * changes.
 *
 * ```typescript
 * import {TrackingFilterDrawerHandle} from './types';
 *
 * ...
 *
 * const drawerRef = useRef<TrackingFilterDrawerHandle>(null);
 * drawerRef.current?.confirmLeave(navigateToReports);
 * ```
 */
export interface TrackingFilterDrawerHandle {
  // runs `continueAction` at once when there are no unapplied changes,
  // otherwise holds it and shows the Apply/Discard prompt - it then runs
  // once the changes are applied or discarded ...
  confirmClose: (continueAction: () => void) => void;
  // runs `leave` at once when there are no unapplied changes, otherwise
  // holds it and shows the Leave Tracking prompt - it then runs once the
  // user chooses to leave ...
  confirmLeave: (leave: () => void) => void;
  // discards the unapplied changes without closing the drawer ...
  discardChanges: () => void;
}

/**
 * Describes one option shown by a current tracking drawer filter.
 *
 * ```typescript
 * import {TrackingDrawerFilterOptionBase} from './types';
 *
 * ...
 *
 * const option: TrackingDrawerFilterOptionBase = {
 *   label   : 'Vehicle',
 *   value   : 'vehicle',
 *   payload : 'vehicle'
 * };
 * ```
 */
export interface TrackingDrawerFilterOptionBase extends FilterChecklistOptionBase {
  payload?: any;
}

/**
 * Defines shared props for current tracking drawer checklist filters.
 *
 * ```typescript
 * import {TrackingDrawerFilterPropsBase} from './types';
 *
 * ...
 *
 * const props: TrackingDrawerFilterPropsBase = {
 *   applyFilter,
 *   loadOptions,
 *   searchPlaceholder
 * };
 * ```
 */
export interface TrackingDrawerFilterPropsBase {
  applyFilter: (selected: any[], trackingMode: TrackingMode) => any;
  emptyState?: ReactNode;
  filterStateKey: TrackingFilterStateKey;
  loadOptions: () => Promise<TrackingDrawerFilterOptionBase[]>;
  onSelectedCountChange?: (count: number) => void;
  onSelectionChange?: () => void;
  searchPlaceholder: string;
  selectedItems?: any[];
  toSelectedValue?: (selected: any) => FilterChecklistValueBase;
}

/**
 * Defines the imperative API exposed by the groups/assets tracking filter.
 *
 * ```typescript
 * import {TrackingGroupsAssetsFilterHandleBase} from './types';
 *
 * ...
 *
 * const filterRef = useRef<TrackingGroupsAssetsFilterHandleBase>(null);
 * filterRef.current?.clear();
 * ```
 */
export interface TrackingGroupsAssetsFilterHandleBase extends TrackingDrawerFilterHandleBase {}

/**
 * Defines props accepted by the groups/assets tracking filter.
 *
 * ```typescript
 * import {TrackingGroupsAssetsFilterPropsBase} from './types';
 *
 * ...
 *
 * const props: TrackingGroupsAssetsFilterPropsBase = {
 *   onSelectedCountChange : setCount
 * };
 * ```
 */
export interface TrackingGroupsAssetsFilterPropsBase {
  onRequestNavigate?: TrackingFilterNavigationRequest;
  onSelectedCountChange?: (count: number) => void;
  onSelectionChange?: () => void;
}

/**
 * Describes the payload shape dispatched for selected groups and assets.
 *
 * ```typescript
 * import {TrackingGroupsAssetsSelectedPayload} from './types';
 *
 * ...
 *
 * const payload: TrackingGroupsAssetsSelectedPayload = {
 *   selectedAssets  : [],
 *   selectedDevices : [],
 *   selectedGroups  : []
 * };
 * ```
 */
export interface TrackingGroupsAssetsSelectedPayload {
  selectedAssets: any[];
  selectedDevices: any[];
  selectedGroups: any[];
}

export type TrackingGroupsAssetsTreeNode = FilterTreeNodeBase;

export type TrackingGroupsAssetsTreeChange = FilterTreeChecklistChangeBase;

/**
 * Describes one group's position within the flattened groups hierarchy
 * used to resolve a search result's ancestor path.
 *
 * ```typescript
 * import {TrackingGroupHierarchyEntry} from './types';
 *
 * ...
 *
 * const entry: TrackingGroupHierarchyEntry = {
 *   id       : '101',
 *   name     : 'Fleet Group',
 *   parentId : null
 * };
 * ```
 */
export interface TrackingGroupHierarchyEntry {
  id: string;
  name: string | undefined;
  parentId: string | null;
}

export type TrackingGroupHierarchyMap = Record<string, TrackingGroupHierarchyEntry>;
